import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { AnimatePresence, motion } from "framer-motion";
import { Zap, Trophy } from "lucide-react";
import { RECORDS, SINGLES, combinedOdds } from "@/lib/data";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const Marquee = () => {
    const [records, setRecords] = useState(RECORDS);
    const [singles, setSingles] = useState(SINGLES);
    const [index, setIndex] = useState(0);

    useEffect(() => {
        axios
            .get(`${API}/doubles`)
            .then((res) => {
                if (Array.isArray(res.data) && res.data.length) setRecords(res.data);
            })
            .catch(() => {});
        axios
            .get(`${API}/singles`)
            .then((res) => {
                if (Array.isArray(res.data) && res.data.length) setSingles(res.data);
            })
            .catch(() => {});
    }, []);

    const feed = useMemo(() => {
        const s = singles
            .filter((x) => x.status !== "nomatch")
            .slice(0, 4)
            .map((x) => ({
                type: "SINGLE",
                date: x.date,
                teams: x.teams,
                pick: x.pick,
                odds: Number(x.odds).toFixed(2),
                status: x.status,
            }));
        const d = records.slice(0, 4).map((x) => ({
            type: "DOUBLE",
            date: x.date,
            teams: `${x.matches[0].teams} & ${x.matches[1].teams}`,
            pick: null,
            odds: combinedOdds(x),
            status: x.status,
        }));
        const merged = [];
        for (let i = 0; i < 4; i++) {
            if (s[i]) merged.push(s[i]);
            if (d[i]) merged.push(d[i]);
        }
        return merged;
    }, [records, singles]);

    useEffect(() => {
        if (feed.length < 2) return;
        const t = setInterval(() => setIndex((i) => (i + 1) % feed.length), 4000);
        return () => clearInterval(t);
    }, [feed.length]);

    if (!feed.length) return null;
    const item = feed[index % feed.length];

    return (
        <div data-testid="editorial-marquee-container" className="relative border-y border-white/5 bg-[#0B0F17]/80">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 py-5 flex items-center gap-4 sm:gap-5">
                <div className="flex items-center gap-2.5 shrink-0">
                    <span className="relative flex h-2 w-2">
                        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-bn-cyan opacity-60" />
                        <span className="relative inline-flex rounded-full h-2 w-2 bg-bn-cyan" />
                    </span>
                    <span className="font-mono text-[10px] tracking-[0.3em] text-bn-cyan font-semibold">RESULTS FEED</span>
                </div>
                <div className="h-8 w-px bg-white/10 shrink-0" />
                <div className="flex-1 min-w-0">
                    <AnimatePresence mode="wait">
                        <motion.div
                            key={index % feed.length}
                            initial={{ opacity: 0, y: 14 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -14 }}
                            transition={{ duration: 0.45, ease: [0.16, 1, 0.3, 1] }}
                            className="flex items-center gap-3 sm:gap-4 min-w-0"
                            data-testid="results-feed-item"
                        >
                            <span className={`shrink-0 inline-flex items-center gap-1.5 font-mono text-[10px] font-bold px-2.5 py-1 rounded-full border ${item.type === "SINGLE" ? "bg-bn-cyan/10 text-bn-cyan border-bn-cyan/30" : "bg-bn-blue/10 text-bn-blue border-bn-blue/30"}`}>
                                {item.type === "SINGLE" ? <Zap className="w-3 h-3" /> : <Trophy className="w-3 h-3" />}
                                {item.type}
                            </span>
                            <span className="font-mono text-xs text-slate-500 shrink-0 hidden sm:inline">{item.date}</span>
                            <span className="text-sm font-semibold text-white truncate">{item.teams}</span>
                            <span className="font-mono text-xs text-bn-cyan shrink-0">{item.pick ? `${item.pick} ` : ""}@ {item.odds}</span>
                            <span className={`shrink-0 font-mono text-[10px] font-bold px-2 py-0.5 rounded border ${item.status === "lost" ? "bg-red-400/10 text-red-400 border-red-400/30" : "bg-bn-green/10 text-bn-green border-bn-green/30"}`}>
                                {item.status === "lost" ? "LOST" : "WON"}
                            </span>
                        </motion.div>
                    </AnimatePresence>
                </div>
                <div className="hidden md:flex items-center gap-1.5 shrink-0">
                    {feed.map((_, i) => (
                        <button
                            key={i}
                            onClick={() => setIndex(i)}
                            aria-label={`Show result ${i + 1}`}
                            data-testid={`results-feed-dot-${i}`}
                            className={`h-1 rounded-full transition-all duration-300 ${i === index % feed.length ? "w-6 bg-bn-blue" : "w-2 bg-white/15 hover:bg-white/30"}`}
                        />
                    ))}
                </div>
            </div>
        </div>
    );
};
